/**
 * The account's numbers in words, in one place, so the summary line, the signals, the tiles and the
 * copied summary say the same thing the same way. The arithmetic (pace, the day the credits run out,
 * the held gap, the pitch) is the API's (AccountMath); this file only words it. Pure.
 */
import type { AccountSummary } from './account-types';

/**
 * A package's name as people read it: "Pro", "Enterprise" (a plan a Paige partner builds; named
 * Custom 2026-10-14, Enterprise again 2026-10-15, Dustin). A retired package on an old row keeps its own name.
 */
export function planName(key: string | null): string {
  if (!key) return 'No plan';
  return key.charAt(0).toUpperCase() + key.slice(1);
}

/** Below this many days of runway the account wants a call; below RUNWAY_DANGER it stops soon. */
export const RUNWAY_WARN = 14;
export const RUNWAY_DANGER = 3;
/** A period younger than this has too few days for its pace to mean much; the words say so. */
export const PACE_SETTLES_DAYS = 7;

export type RunwayTone = 'danger' | 'warning' | 'neutral';

export function runwayTone(days: number | null): RunwayTone {
  if (days === null) return 'neutral';
  if (days <= RUNWAY_DANGER) return 'danger';
  return days < RUNWAY_WARN ? 'warning' : 'neutral';
}

/** "about 3 days", "out now", "more than a year"; null burn reads "not burning". */
export function runwayWord(days: number | null): string {
  if (days === null) return 'not burning';
  if (days <= 0) return 'out now';
  if (days === 1) return 'about 1 day';
  return days > 365 ? 'more than a year' : `about ${days} days`;
}

/** "457%" for 4.57; "—" without a plan. */
export function percentWord(share: number | null): string {
  return share === null ? '—' : `${Math.round(share * 100)}%`;
}

/** "101%", or "405% (4×)" from twice the plan up, so the multiple is never left to the reader. */
export function paceWord(pace: number | null): string {
  if (pace === null) return '—';
  return pace >= 2 ? `${percentWord(pace)} (${Math.floor(pace)}×)` : percentWord(pace);
}

/** "Oct 14" for 2026-10-14, in the reader's language. */
export function dayWord(day: string, locale?: string): string {
  return new Date(`${day}T12:00:00Z`).toLocaleDateString(locale ?? 'en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/** Whole days between two yyyy-MM-dd days. */
export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

/** Days into the period, so "3 days in" can qualify a pace that has not settled. */
export function periodAge(a: AccountSummary, today: string): number {
  return Math.max(0, daysBetween(a.plan.periodStart, today));
}

/** True when the credits run out before the refill: the company is (or will be) held until it. */
export function heldGap(a: AccountSummary): { from: string; until: string; days: number } | null {
  const c = a.credits;
  if (!c.runsOutOn || c.heldDays <= 0) return null;
  return { from: c.runsOutOn, until: c.periodEndsOn, days: c.heldDays };
}

/**
 * The credits in one sentence, said once on the page (the summary line): "Out of credits Oct 14,
 * refill Oct 15: held about 1 day." Null when the credits last past the refill.
 */
export function creditSentence(a: AccountSummary, locale?: string): string | null {
  const gap = heldGap(a);
  const day = (d: string) => dayWord(d, locale);
  if (a.credits.held) {
    return gap
      ? `Held for pages until the refill on ${day(gap.until)}, ${gap.days} day${gap.days === 1 ? '' : 's'} away.`
      : 'Held for pages now.';
  }
  if (!gap) return null;
  return `Out of pages ${day(gap.from)}, refill ${day(gap.until)}: held about ${gap.days} day${gap.days === 1 ? '' : 's'}.`;
}

/** "Business, +$70 a month" or "Enterprise"; null within plan. */
export function upgradeWord(a: AccountSummary): string | null {
  const p = a.pitch;
  if (!p.upgradeTo) return null;
  const more = p.upgradeDifferenceDollars;
  return more === null
    ? planName(p.upgradeTo)
    : `${planName(p.upgradeTo)}, +$${more.toFixed(0)} a month`;
}

/** "packs to the refill: 1,000 credits, $20"; null when nothing would be held. */
export function packsWord(a: AccountSummary, int: (n: number) => string): string | null {
  const p = a.pitch;
  if (p.packsToRefillCredits <= 0) return null;
  return `packs to the refill: ${int(p.packsToRefillCredits)} pages, $${p.packsToRefillDollars.toFixed(0)}`;
}

/** A pace from this far over the plan wears the warning edge; one percent over is not a warning on its own. */
export const PACE_WARN = 1.1;

/**
 * The pitch in one line, the call today first (round 3, sales M1): "Packs now: 1,000 credits, $20 ·
 * Business next, +$120 a month, cheaper than packs from 6,000 credits over"; null with nothing to sell.
 */
export function pitchWord(a: AccountSummary, int: (n: number) => string): string | null {
  const p = a.pitch;
  const packs = packsWord(a, int);
  const plan = upgradeWord(a);
  if (!plan && !packs) return null;
  if (p.recommend === 'upgrade' && plan)
    return `${plan} now${p.periodsOverPlan >= 2 ? ': over plan two periods running' : ''}`;
  const next = plan
    ? `${plan} next${p.breakEvenCredits ? `, cheaper than packs from ${int(p.breakEvenCredits)} pages over a period` : ''}`
    : null;
  return [packs ? `Packs now: ${packs.replace('packs to the refill: ', '')}` : null, next]
    .filter(Boolean)
    .join(' · ');
}
